/**
 * MultiFactorPalmDetector - Advanced Palm Rejection Engine
 * 
 * Phase 1 Implementation: Enhanced algorithms for 85-90% accuracy
 * Replaces simple area-based rejection with comprehensive multi-factor analysis
 */

class MultiFactorPalmDetector {
    constructor(userSettings = {}) {
        this.settings = {
            // Contact area thresholds (device-calibrated)
            palmAreaThreshold: userSettings.palmAreaThreshold || 200,
            fingerAreaThreshold: userSettings.fingerAreaThreshold || 100,
            stylusAreaThreshold: userSettings.stylusAreaThreshold || 50,
            
            // Pressure thresholds  
            palmPressureMax: userSettings.palmPressureMax || 0.3,
            palmPressureMin: userSettings.palmPressureMin || 0.0,
            stylusPressureMin: userSettings.stylusPressureMin || 0.1,
            
            // Velocity thresholds (pixels per frame)
            palmVelocityMax: userSettings.palmVelocityMax || 5,
            stylusVelocityMin: userSettings.stylusVelocityMin || 2,
            fingerVelocityTypical: userSettings.fingerVelocityTypical || 8,
            
            // Confidence weights for different factors
            weights: {
                contactArea: userSettings.areaWeight || 0.30,
                pressure: userSettings.pressureWeight || 0.25,
                velocity: userSettings.velocityWeight || 0.20,
                temporal: userSettings.temporalWeight || 0.15,
                multiTouch: userSettings.multiTouchWeight || 0.10
            },
            
            // Device-specific adjustments
            deviceType: userSettings.deviceType || 'unknown',
            touchSensitivity: userSettings.touchSensitivity || 1.0,
            
            // Sensitivity levels
            sensitivity: userSettings.sensitivity || 'medium' // 'low', 'medium', 'high', 'very-high'
        };
        
        this.touchHistory = new Map(); // Track touch sequences for temporal analysis
        this.recentDecisions = []; // Track recent decisions for learning
        this.calibrationData = userSettings.calibrationData || null;
        
    }
    
    /**
     * Main detection function - analyzes a touch event for palm probability
     * @param {Object} touchEvent - Pointer/Touch event object
     * @param {Object} context - Additional context (active touches, drawing state, etc.)
     * @returns {Object} Analysis with palm confidence and detailed breakdown
     */
    analyzeTouch(touchEvent, context = {}) {
        const startTime = performance.now();
        
        try {
            // Prepare touch data
            const touchData = this.prepareTouchData(touchEvent, context);
            
            // Run all detection factors
            const analysis = {
                touchId: touchData.id,
                timestamp: Date.now(),
                
                // Factor 1: Contact Area Analysis (30% weight)
                contactArea: this.analyzeContactArea(touchData),
                
                // Factor 2: Pressure Pattern Analysis (25% weight)
                pressure: this.analyzePressure(touchData, context),
                
                // Factor 3: Velocity Analysis (20% weight)
                velocity: this.analyzeVelocity(touchData, context),
                
                // Factor 4: Multi-touch Context (10% weight)
                multiTouch: this.analyzeMultiTouchContext(touchData, context),
                
                // Factor 5: Device-specific Analysis (15% weight)
                deviceSpecific: this.analyzeDeviceSpecific(touchData, context),
                
                // Raw confidence before weighting
                confidence: 0,
                
                // Decision metadata
                processingTime: 0,
                reasoning: []
            };
            
            // Calculate weighted palm confidence
            analysis.confidence = this.calculateWeightedConfidence(analysis);
            
            // Store for temporal analysis
            this.updateTouchHistory(touchData.id, touchData, analysis);
            
            // Record decision for learning
            this.recordDecision(analysis);
            
            analysis.processingTime = performance.now() - startTime;
            
            return analysis;
            
        } catch (error) {
            console.error('Error in palm detection analysis:', error);
            // Fallback to safe default
            return {
                confidence: 0.5, // Uncertain
                error: error.message,
                reasoning: ['Error in analysis - defaulting to uncertain']
            };
        }
    }
    
    /**
     * Prepare normalized touch data from event
     */
    prepareTouchData(touchEvent, context) {
        return {
            id: touchEvent.pointerId || touchEvent.identifier || 'unknown',
            x: touchEvent.clientX || touchEvent.pageX || 0,
            y: touchEvent.clientY || touchEvent.pageY || 0,
            width: touchEvent.width || (touchEvent.radiusX * 2) || 0,
            height: touchEvent.height || (touchEvent.radiusY * 2) || 0,
            pressure: touchEvent.pressure || touchEvent.force || 0.5,
            pointerType: touchEvent.pointerType || 'touch',
            tiltX: touchEvent.tiltX || 0,
            tiltY: touchEvent.tiltY || 0,
            timestamp: Date.now(),
            
            // Context data
            isDrawing: context.isDrawing || false,
            activeTouchCount: context.activeTouchCount || 1,
            primaryTouchId: context.primaryTouchId || null
        };
    }
    
    /**
     * Factor 1: Contact Area Analysis (30% weight)
     * Analyzes the size and shape of the touch contact
     */
    analyzeContactArea(touchData) {
        const area = touchData.width * touchData.height;
        const aspectRatio = touchData.width > 0 && touchData.height > 0 ? 
            Math.max(touchData.width, touchData.height) / Math.min(touchData.width, touchData.height) : 1.0;
        
        let palmProbability = 0;
        const reasoning = [];
        
        // Area-based classification
        if (area > this.settings.palmAreaThreshold * 1.5) {
            palmProbability += 0.8; // Very likely palm
            reasoning.push(`Very large contact area: ${area.toFixed(0)}px²`);
        } else if (area > this.settings.palmAreaThreshold) {
            palmProbability += 0.6; // Likely palm
            reasoning.push(`Large contact area: ${area.toFixed(0)}px²`);
        } else if (area > this.settings.fingerAreaThreshold) {
            palmProbability += 0.3; // Possible finger/small palm
            reasoning.push(`Medium contact area: ${area.toFixed(0)}px²`);
        } else if (area > this.settings.stylusAreaThreshold) {
            palmProbability += 0.1; // Likely finger
            reasoning.push(`Small contact area: ${area.toFixed(0)}px²`);
        } else {
            palmProbability = 0.05; // Very likely stylus
            reasoning.push(`Very small contact area: ${area.toFixed(0)}px² - likely stylus`);
        }
        
        // Aspect ratio analysis (palms often have elongated contact)
        if (aspectRatio > 2.0) {
            palmProbability += 0.2;
            reasoning.push(`Elongated contact (${aspectRatio.toFixed(1)}:1) suggests palm`);
        } else if (aspectRatio > 1.5) {
            palmProbability += 0.1;
            reasoning.push(`Slightly elongated contact (${aspectRatio.toFixed(1)}:1)`);
        }
        
        // Adjust for device sensitivity
        palmProbability *= this.settings.touchSensitivity;
        
        return {
            area,
            aspectRatio,
            palmProbability: Math.min(1.0, palmProbability),
            reasoning,
            confidence: Math.min(1.0, palmProbability)
        };
    }
    
    /**
     * Factor 2: Pressure Pattern Analysis (25% weight)
     * Analyzes pressure characteristics and patterns
     */
    analyzePressure(touchData, context) {
        const pressure = touchData.pressure;
        let palmProbability = 0;
        const reasoning = [];
        
        // Get pressure history for this touch
        const history = this.touchHistory.get(touchData.id) || [];
        const recentPressures = history.slice(-5).map(h => h.touchData.pressure);
        
        // Pressure level analysis
        if (pressure < this.settings.palmPressureMin) {
            palmProbability += 0.1;
            reasoning.push(`Very low pressure (${pressure.toFixed(3)}) - possible hover/light contact`);
        } else if (pressure < this.settings.palmPressureMax) {
            palmProbability += 0.6;
            reasoning.push(`Low pressure (${pressure.toFixed(3)}) typical of palm resting`);
        } else if (pressure > 0.7) {
            palmProbability += 0.2;
            reasoning.push(`High pressure (${pressure.toFixed(3)}) - could be intentional or heavy palm`);
        } else {
            palmProbability += 0.1;
            reasoning.push(`Medium pressure (${pressure.toFixed(3)}) - likely intentional touch`);
        }
        
        // Pressure consistency analysis (palms tend to have steady pressure)
        if (recentPressures.length >= 3) {
            const pressureVariance = this.calculateVariance(recentPressures);
            if (pressureVariance < 0.05) {
                palmProbability += 0.3;
                reasoning.push(`Consistent pressure pattern (var: ${pressureVariance.toFixed(3)}) suggests palm resting`);
            } else if (pressureVariance > 0.2) {
                palmProbability -= 0.2; // Variable pressure suggests intentional control
                reasoning.push(`Variable pressure (var: ${pressureVariance.toFixed(3)}) suggests intentional drawing`);
            }
        }
        
        // Stylus detection (high confidence when pressure > 0.8 and pointerType is pen)
        if (touchData.pointerType === 'pen' && pressure > 0.8) {
            palmProbability = 0.1; // Override - very likely stylus
            reasoning.push('High-pressure pen input detected - overriding palm probability');
        }
        
        return {
            pressure,
            pressureVariance: recentPressures.length >= 3 ? this.calculateVariance(recentPressures) : 0,
            palmProbability: Math.max(0, Math.min(1.0, palmProbability)),
            reasoning,
            confidence: Math.min(1.0, Math.abs(palmProbability - 0.5) * 2) // Confidence based on how far from uncertain
        };
    }
    
    /**
     * Factor 3: Velocity Analysis (20% weight)  
     * Analyzes movement speed and acceleration patterns
     */
    analyzeVelocity(touchData, context) {
        let palmProbability = 0;
        const reasoning = [];
        
        // Get movement history
        const history = this.touchHistory.get(touchData.id) || [];
        if (history.length < 2) {
            return {
                velocity: 0,
                acceleration: 0,
                palmProbability: 0.5, // Uncertain without movement data
                reasoning: ['Insufficient movement data for velocity analysis'],
                confidence: 0.2
            };
        }
        
        // Calculate velocity from recent positions
        const recent = history.slice(-3);
        const velocities = [];
        
        for (let i = 1; i < recent.length; i++) {
            const dt = recent[i].timestamp - recent[i-1].timestamp;
            if (dt > 0) {
                const dx = recent[i].touchData.x - recent[i-1].touchData.x;
                const dy = recent[i].touchData.y - recent[i-1].touchData.y;
                const velocity = Math.sqrt(dx*dx + dy*dy) / dt * 1000; // pixels per second
                velocities.push(velocity);
            }
        }
        
        if (velocities.length === 0) {
            return {
                velocity: 0,
                palmProbability: 0.5,
                reasoning: ['No valid velocity data'],
                confidence: 0.2
            };
        }
        
        const avgVelocity = velocities.reduce((sum, v) => sum + v, 0) / velocities.length;
        
        // Velocity-based classification
        if (avgVelocity < this.settings.palmVelocityMax) {
            palmProbability += 0.7;
            reasoning.push(`Very low velocity (${avgVelocity.toFixed(1)}px/s) suggests stationary palm`);
        } else if (avgVelocity < this.settings.stylusVelocityMin * 2) {
            palmProbability += 0.4;
            reasoning.push(`Low velocity (${avgVelocity.toFixed(1)}px/s) could be slow palm movement`);
        } else if (avgVelocity > this.settings.fingerVelocityTypical) {
            palmProbability -= 0.3;
            reasoning.push(`High velocity (${avgVelocity.toFixed(1)}px/s) suggests intentional movement`);
        } else {
            palmProbability += 0.1;
            reasoning.push(`Medium velocity (${avgVelocity.toFixed(1)}px/s) - ambiguous`);
        }
        
        // Acceleration analysis (smooth movement vs erratic)
        if (velocities.length >= 2) {
            const accelerations = [];
            for (let i = 1; i < velocities.length; i++) {
                accelerations.push(Math.abs(velocities[i] - velocities[i-1]));
            }
            const avgAcceleration = accelerations.reduce((sum, a) => sum + a, 0) / accelerations.length;
            
            if (avgAcceleration < 50) {
                palmProbability += 0.2;
                reasoning.push(`Smooth movement (accel: ${avgAcceleration.toFixed(1)}) suggests palm drift`);
            } else if (avgAcceleration > 200) {
                palmProbability -= 0.2;
                reasoning.push(`Erratic movement (accel: ${avgAcceleration.toFixed(1)}) suggests intentional drawing`);
            }
        }
        
        return {
            velocity: avgVelocity,
            acceleration: velocities.length >= 2 ? 
                Math.abs(velocities[velocities.length-1] - velocities[velocities.length-2]) : 0,
            palmProbability: Math.max(0, Math.min(1.0, palmProbability)),
            reasoning,
            confidence: Math.min(1.0, velocities.length / 3.0) // More confident with more data
        };
    }
    
    /**
     * Factor 4: Multi-touch Context Analysis (10% weight)
     * Analyzes relationship with other active touches
     */
    analyzeMultiTouchContext(touchData, context) {
        let palmProbability = 0;
        const reasoning = [];
        
        const activeTouchCount = context.activeTouchCount || 1;
        
        if (activeTouchCount === 1) {
            // Single touch - neutral
            reasoning.push('Single touch - no multi-touch context');
            return {
                activeTouchCount,
                palmProbability: 0.5,
                reasoning,
                confidence: 0.3
            };
        }
        
        // Multi-touch scenarios
        if (activeTouchCount > 3) {
            palmProbability += 0.8;
            reasoning.push(`Many touches (${activeTouchCount}) - likely palm + stylus scenario`);
        } else if (activeTouchCount === 2) {
            // Two touches could be stylus + palm
            if (context.primaryTouchId && touchData.id !== context.primaryTouchId) {
                palmProbability += 0.6;
                reasoning.push('Secondary touch in two-touch scenario - likely palm');
            } else {
                palmProbability += 0.3;
                reasoning.push('Primary touch in two-touch scenario - uncertain');
            }
        } else {
            palmProbability += 0.5;
            reasoning.push(`Multi-touch scenario (${activeTouchCount} touches) - elevated palm probability`);
        }
        
        // If stylus is already active, non-stylus touches are very likely palm
        if (touchData.pointerType !== 'pen' && context.hasStylusActive) {
            palmProbability += 0.7;
            reasoning.push('Non-stylus touch while stylus active - very likely palm');
        }
        
        return {
            activeTouchCount,
            palmProbability: Math.min(1.0, palmProbability),
            reasoning,
            confidence: 0.8 // High confidence in multi-touch analysis
        };
    }
    
    /**
     * Factor 5: Device-specific Analysis (15% weight)
     * Applies device-specific optimizations and patterns
     */
    analyzeDeviceSpecific(touchData, context) {
        let palmProbability = 0;
        const reasoning = [];
        
        // Device type adjustments
        if (this.settings.deviceType === 'iPad') {
            // iPad-specific optimizations
            if (touchData.width > 30 || touchData.height > 30) {
                palmProbability += 0.6;
                reasoning.push('Large contact on iPad - likely palm');
            }
        } else if (this.settings.deviceType === 'Surface') {
            // Surface-specific optimizations
            if (touchData.pointerType === 'pen') {
                palmProbability = 0.1; // High confidence stylus
                reasoning.push('Surface Pen detected - very low palm probability');
            }
        } else if (this.settings.deviceType === 'Android') {
            // Android tablet optimizations
            if (touchData.pressure === 0.5) {
                // Many Android devices don't report real pressure
                palmProbability += 0.1;
                reasoning.push('Default pressure on Android - relying more on area');
            }
        }
        
        // Edge detection (palms often contact screen edges)
        const screenEdgeThreshold = 50; // pixels from edge
        if (context.canvasWidth && context.canvasHeight) {
            const nearLeftEdge = touchData.x < screenEdgeThreshold;
            const nearRightEdge = touchData.x > (context.canvasWidth - screenEdgeThreshold);
            const nearTopEdge = touchData.y < screenEdgeThreshold;
            const nearBottomEdge = touchData.y > (context.canvasHeight - screenEdgeThreshold);
            
            if (nearLeftEdge || nearRightEdge || nearTopEdge || nearBottomEdge) {
                palmProbability += 0.3;
                reasoning.push('Touch near screen edge - increased palm probability');
            }
        }
        
        // Tilt analysis (stylus often has tilt, palm/finger usually don't)
        if (Math.abs(touchData.tiltX) > 10 || Math.abs(touchData.tiltY) > 10) {
            palmProbability -= 0.4; // Likely stylus
            reasoning.push(`Tilt detected (${touchData.tiltX.toFixed(1)}, ${touchData.tiltY.toFixed(1)}) - likely stylus`);
        }
        
        return {
            deviceType: this.settings.deviceType,
            nearEdge: false, // Could calculate this
            palmProbability: Math.max(0, Math.min(1.0, palmProbability)),
            reasoning,
            confidence: 0.7
        };
    }
    
    /**
     * Calculate weighted confidence from all factors
     */
    calculateWeightedConfidence(analysis) {
        let totalConfidence = 0;
        let totalWeight = 0;
        
        // Apply weights to each factor
        const factors = [
            { value: analysis.contactArea.palmProbability, weight: this.settings.weights.contactArea, confidence: analysis.contactArea.confidence || 1.0 },
            { value: analysis.pressure.palmProbability, weight: this.settings.weights.pressure, confidence: analysis.pressure.confidence || 1.0 },
            { value: analysis.velocity.palmProbability, weight: this.settings.weights.velocity, confidence: analysis.velocity.confidence || 1.0 },
            { value: analysis.multiTouch.palmProbability, weight: this.settings.weights.multiTouch, confidence: analysis.multiTouch.confidence || 1.0 },
            { value: analysis.deviceSpecific.palmProbability, weight: this.settings.weights.temporal, confidence: analysis.deviceSpecific.confidence || 1.0 }
        ];
        
        factors.forEach(factor => {
            const effectiveWeight = factor.weight * factor.confidence;
            totalConfidence += factor.value * effectiveWeight;
            totalWeight += effectiveWeight;
        });
        
        const baseConfidence = totalWeight > 0 ? totalConfidence / totalWeight : 0.5;
        
        // Apply sensitivity adjustment
        const sensitivityMultiplier = this.getSensitivityMultiplier();
        let adjustedConfidence = baseConfidence * sensitivityMultiplier;
        
        // Apply user calibration if available
        if (this.calibrationData) {
            adjustedConfidence = this.applyUserCalibration(adjustedConfidence, analysis);
        }
        
        // Build reasoning summary
        analysis.reasoning = this.buildReasoningSummary(analysis, baseConfidence, adjustedConfidence);
        
        return Math.max(0, Math.min(1.0, adjustedConfidence));
    }
    
    /**
     * Get sensitivity multiplier based on user settings
     */
    getSensitivityMultiplier() {
        switch (this.settings.sensitivity) {
            case 'low': return 0.7;
            case 'medium': return 1.0;
            case 'high': return 1.3;
            case 'very-high': return 1.6;
            default: return 1.0;
        }
    }
    
    /**
     * Apply user calibration adjustments
     */
    applyUserCalibration(confidence, analysis) {
        if (!this.calibrationData) return confidence;
        
        // Apply user-specific adjustments based on calibration
        let adjustedConfidence = confidence;
        
        // Area threshold adjustment
        if (this.calibrationData.areaAdjustment) {
            adjustedConfidence *= this.calibrationData.areaAdjustment;
        }
        
        // Pressure threshold adjustment
        if (this.calibrationData.pressureAdjustment) {
            adjustedConfidence *= this.calibrationData.pressureAdjustment;
        }
        
        return Math.max(0, Math.min(1.0, adjustedConfidence));
    }
    
    /**
     * Build human-readable reasoning summary
     */
    buildReasoningSummary(analysis, baseConfidence, finalConfidence) {
        const summary = [];
        
        // Top contributing factors
        const factors = [
            { name: 'Contact Area', prob: analysis.contactArea.palmProbability, weight: this.settings.weights.contactArea },
            { name: 'Pressure', prob: analysis.pressure.palmProbability, weight: this.settings.weights.pressure },
            { name: 'Velocity', prob: analysis.velocity.palmProbability, weight: this.settings.weights.velocity },
            { name: 'Multi-touch', prob: analysis.multiTouch.palmProbability, weight: this.settings.weights.multiTouch },
            { name: 'Device-specific', prob: analysis.deviceSpecific.palmProbability, weight: this.settings.weights.temporal }
        ];
        
        // Sort by weighted contribution
        factors.sort((a, b) => (b.prob * b.weight) - (a.prob * a.weight));
        
        // Add top 2 factors to summary
        if (factors[0].prob > 0.5) {
            summary.push(`${factors[0].name}: ${(factors[0].prob * 100).toFixed(0)}% palm probability`);
        }
        if (factors[1].prob > 0.5) {
            summary.push(`${factors[1].name}: ${(factors[1].prob * 100).toFixed(0)}% palm probability`);
        }
        
        // Add confidence level
        summary.push(`Final confidence: ${(finalConfidence * 100).toFixed(1)}%`);
        
        return summary;
    }
    
    /**
     * Update touch history for temporal analysis
     */
    updateTouchHistory(touchId, touchData, analysis) {
        if (!this.touchHistory.has(touchId)) {
            this.touchHistory.set(touchId, []);
        }
        
        const history = this.touchHistory.get(touchId);
        history.push({
            timestamp: Date.now(),
            touchData,
            analysis
        });
        
        // Keep only recent history (last 2 seconds)
        const cutoffTime = Date.now() - 2000;
        const recentHistory = history.filter(item => item.timestamp > cutoffTime);
        this.touchHistory.set(touchId, recentHistory);
        
        // Clean up old touch histories
        for (const [id, hist] of this.touchHistory.entries()) {
            if (hist.length === 0 || hist[hist.length - 1].timestamp < cutoffTime) {
                this.touchHistory.delete(id);
            }
        }
    }
    
    /**
     * Record decision for learning and analytics
     */
    recordDecision(analysis) {
        this.recentDecisions.push({
            timestamp: Date.now(),
            confidence: analysis.confidence,
            factors: {
                contactArea: analysis.contactArea.palmProbability,
                pressure: analysis.pressure.palmProbability,
                velocity: analysis.velocity.palmProbability,
                multiTouch: analysis.multiTouch.palmProbability,
                deviceSpecific: analysis.deviceSpecific.palmProbability
            }
        });
        
        // Keep only recent decisions (last 100)
        if (this.recentDecisions.length > 100) {
            this.recentDecisions = this.recentDecisions.slice(-100);
        }
    }
    
    /**
     * Utility function to calculate variance
     */
    calculateVariance(values) {
        if (values.length < 2) return 0;
        
        const mean = values.reduce((sum, val) => sum + val, 0) / values.length;
        const squaredDiffs = values.map(val => Math.pow(val - mean, 2));
        return squaredDiffs.reduce((sum, diff) => sum + diff, 0) / values.length;
    }
    
    /**
     * Clean up touch history when touch ends
     */
    cleanupTouch(touchId) {
        this.touchHistory.delete(touchId);
    }
    
    /**
     * Get performance metrics
     */
    getPerformanceMetrics() {
        return {
            activeTouches: this.touchHistory.size,
            recentDecisions: this.recentDecisions.length,
            averageProcessingTime: this.recentDecisions.length > 0 ? 
                this.recentDecisions.reduce((sum, d) => sum + (d.processingTime || 0), 0) / this.recentDecisions.length : 0,
            settings: this.settings
        };
    }
}

export default MultiFactorPalmDetector;
